/**
 * <purchase-options> — variant-aware subscribe / one-time selector.
 *
 * Responsibilities:
 *  1. Toggle the hidden `selling_plan` input on the add-to-cart form when the
 *     customer switches between Subscribe and One-time.
 *  2. When the selected variant changes, swap the displayed subscribe price,
 *     one-time price, compare-at prices, savings badge, selling-plan id and an
 *     optional per-variant note — so the widget can show different pricing and
 *     copy per variant (e.g. 10-pack vs 30-pack, future flavors/merch).
 *
 * All pricing is pre-formatted server-side per variant in a JSON data island,
 * which keeps money formatting correct and avoids re-deriving discounts in JS.
 */
class PurchaseOptions extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.section;

    try {
      const dataEl = this.querySelector('[data-purchase-options-data]');
      this.variants = dataEl ? JSON.parse(dataEl.textContent) : {};
    } catch (e) {
      this.variants = {};
    }

    this.subscribeRadio = this.querySelector('[data-po-subscribe-radio]');
    this.onetimeRadio = this.querySelector('[data-po-onetime-radio]');
    this.els = {
      subscribePrice: this.querySelector('[data-po-subscribe-price]'),
      subscribeCompare: this.querySelector('[data-po-subscribe-compare]'),
      saveBadge: this.querySelector('[data-po-save-badge]'),
      onetimePrice: this.querySelector('[data-po-onetime-price]'),
      onetimeCompare: this.querySelector('[data-po-onetime-compare]'),
      note: this.querySelector('[data-po-note]'),
      cadence: this.querySelector('[data-po-cadence]'),
    };

    this.onOptionChange = this.onOptionChange.bind(this);
  }

  connectedCallback() {
    this.bindForm();

    [this.subscribeRadio, this.onetimeRadio].forEach((radio) => {
      radio?.addEventListener('change', this.onOptionChange);
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const data = event?.data;
        if (!data || String(data.sectionId) !== String(this.sectionId)) return;
        if (data.variant && data.variant.id) this.updateForVariant(data.variant.id);
      });
    }

    this.syncInitial();

    // If the form wasn't in the DOM yet (block/script ordering), try again next
    // frame so the initial selling_plan is applied before any add-to-cart.
    if (!this.productForm) {
      requestAnimationFrame(() => {
        this.bindForm();
        this.syncInitial();
      });
    }
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  /** Locate the product form and guarantee the plan is correct at submit time. */
  bindForm() {
    if (this.productForm) return;
    this.productForm =
      document.getElementById(`product-form-${this.sectionId}`) ||
      document.querySelector(`#ProductOptimized-${this.sectionId} form[id^="product-form"]`) ||
      this.closest('product-info')?.querySelector('form[id^="product-form"]') ||
      null;

    if (this.productForm && !this.submitBound) {
      // Capture phase runs before product-form.js reads the FormData, so the
      // selling_plan is always current when the cart request is built.
      this.productForm.addEventListener('submit', () => this.applySellingPlan(), true);
      this.submitBound = true;
    }
  }

  syncInitial() {
    const initialId = this.getCurrentVariantId();
    if (initialId) this.updateForVariant(initialId);
    this.applySellingPlan();
  }

  getCurrentVariantId() {
    const input = this.productForm?.querySelector('input[name="id"]');
    return input ? input.value : null;
  }

  onOptionChange() {
    this.applySellingPlan();
  }

  /** Add/remove the hidden selling_plan input based on the chosen option. */
  applySellingPlan() {
    if (!this.productForm) return;
    let sellingPlanInput = this.productForm.querySelector('input[name="selling_plan"]');
    const subscribing = this.subscribeRadio && this.subscribeRadio.checked;
    const planId = this.subscribeRadio ? this.subscribeRadio.dataset.sellingPlanId : '';

    if (subscribing && planId) {
      if (!sellingPlanInput) {
        sellingPlanInput = document.createElement('input');
        sellingPlanInput.type = 'hidden';
        sellingPlanInput.name = 'selling_plan';
        this.productForm.appendChild(sellingPlanInput);
      }
      sellingPlanInput.value = planId;
    } else if (sellingPlanInput) {
      sellingPlanInput.remove();
    }
  }

  /** Swap all per-variant text/pricing when the variant changes. */
  updateForVariant(variantId) {
    const v = this.variants[variantId];
    if (!v) return;

    // Keep the subscribe radio's plan id current, then re-apply to the form.
    if (this.subscribeRadio) {
      if (v.has_plan && v.selling_plan_id != null) {
        this.subscribeRadio.dataset.sellingPlanId = v.selling_plan_id;
        this.subscribeRadio.disabled = false;
      } else {
        this.subscribeRadio.dataset.sellingPlanId = '';
        // No plan for this variant — fall back to one-time.
        this.subscribeRadio.disabled = true;
        if (this.subscribeRadio.checked && this.onetimeRadio) this.onetimeRadio.checked = true;
      }
    }

    this.setText(this.els.subscribePrice, v.subscribe_price ?? v.onetime_price);
    this.setOptional(this.els.subscribeCompare, v.subscribe_compare);
    this.setOptional(this.els.onetimeCompare, v.onetime_compare);
    this.setText(this.els.onetimePrice, v.onetime_price);

    if (this.els.saveBadge) {
      if (v.save_badge) {
        this.els.saveBadge.textContent = `SAVE ${v.save_badge}%`;
        this.els.saveBadge.hidden = false;
      } else {
        this.els.saveBadge.hidden = true;
      }
    }

    if (this.els.note) {
      if (v.note) {
        this.els.note.textContent = v.note;
        this.els.note.hidden = false;
      } else {
        this.els.note.hidden = true;
      }
    }

    if (this.els.cadence) {
      const cadenceRow = this.els.cadence.closest('.po__perk--cadence') || this.els.cadence;
      if (v.selling_plan_name) {
        this.els.cadence.textContent = v.selling_plan_name;
        cadenceRow.hidden = false;
      } else {
        cadenceRow.hidden = true;
      }
    }

    this.applySellingPlan();
  }

  setText(el, value) {
    if (el && value != null) el.textContent = value;
  }

  setOptional(el, value) {
    if (!el) return;
    if (value) {
      el.textContent = value;
      el.hidden = false;
    } else {
      el.hidden = true;
    }
  }
}

if (!customElements.get('purchase-options')) {
  customElements.define('purchase-options', PurchaseOptions);
}
